/**
 * Expedição: a fila do que ficou pronto e ainda não saiu da gráfica.
 *
 * É a última tela do ciclo da OS e a única que a fecha: enquanto ninguém
 * registrar a saída, a OS fica `pronta` — produzida, mas ainda na gráfica. É
 * `registrarExpedicao` que a leva para `concluida`.
 *
 * ⚠️ A DISTINÇÃO ENTRE "PRONTA" E "CONCLUÍDA" É O MOTIVO DESTA TELA EXISTIR.
 * Sem ela, "acabou a produção" e "o cliente recebeu" viram a mesma coisa, e a
 * pergunta que o Jeferson faz todo dia — "o que está pronto esperando sair?" —
 * não tem resposta em lugar nenhum. O parâmetro `prazoContaAte` decide qual dos
 * dois momentos conta para pontualidade (RN-031); as duas datas são gravadas de
 * qualquer jeito, então a resposta pode mudar depois sem perder histórico.
 *
 * A OS traz do ERP o que foi combinado com o cliente (`entregaTipo`,
 * `transportadora`). Isso é **sugestão**, não trava: quem despacha vê o que
 * estava previsto e registra o que aconteceu de verdade.
 */

import { formato, ordenarParaExpedicao, type Expedicao } from '@pcp/dominio';
import type { LinhaCarteira } from '@pcp/dados';
import {
  botao,
  h,
  marcasDaOs,
  notificar,
  render,
  sf,
  tituloDeSecao,
  vazio,
} from '@pcp/ui';
import type { Aplicacao } from '../estado.js';
import { folhaDeExpedicao } from '../dialogos.js';

export async function telaExpedicao(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const [carteira, expedicoes] = await Promise.all([app.repo.carteira(), app.repo.expedicoes()]);

  const prontas = ordenarParaExpedicao(
    carteira
      .filter((l) => l.os.status === 'pronta')
      .map((l) => ({ ...l, numero: l.os.numero, previsaoEntregaEm: l.os.previsaoEntregaEm })),
  );

  // As últimas saídas ficam à vista porque o erro mais comum aqui é registrar
  // a OS errada, e o jeito de perceber é ver o que acabou de sair.
  const recentes = [...expedicoes]
    .sort((a, b) => b.ocorridoEm.localeCompare(a.ocorridoEm))
    .slice(0, 15);

  render(destino, [
    h('.vstack.gap-4', [
      h('.ph', [
        h('div', [
          h('p.olho', 'PRODUÇÃO'),
          h('h1', 'Expedição'),
          h(
            'p.lead',
            'O que a fábrica terminou e ainda está na gráfica. Registrar a saída é o que fecha a OS.',
          ),
        ]),
      ]),

      prontas.length === 0
        ? h('.cartao', vazio(
            'shippingbox',
            'Nada esperando saída',
            'Quando a última etapa de uma OS for concluída, ela aparece aqui para ser despachada ou retirada.',
          ))
        : filaDeProntas(app, prontas),

      recentes.length > 0 ? blocoRecentes(app, recentes, carteira) : null,
    ]),
  ]);
}

function filaDeProntas(app: Aplicacao, prontas: LinhaCarteira[]): HTMLElement {
  return h('.bloco', [
    h('.bloco-h', [
      h('div', [
        h('p.bloco-t', [sf('shippingbox'), `Prontas para sair · ${prontas.length}`]),
        h('p.bloco-d', 'Ordenadas pela promessa ao cliente. As atrasadas vêm primeiro.'),
      ]),
    ]),
    h('.tab-rolagem', [
      h('table.tab', [
        h('thead', h('tr', [
          h('th', { style: { width: '74px' } }, 'OS'),
          h('th', { style: { width: '170px' } }, 'Cliente'),
          h('th', 'Produto'),
          h('th', { style: { width: '150px' } }, 'Combinado no ERP'),
          h('th', { style: { width: '130px' } }, 'Prometida'),
          h('th', { style: { width: '132px' } }, 'Pronta desde'),
          h('th', { style: { width: '150px' } }, ''),
        ])),
        h('tbody', prontas.map((l) => linhaPronta(app, l))),
      ]),
    ]),
  ]);
}

function linhaPronta(app: Aplicacao, l: LinhaCarteira): HTMLElement {
  const combinado = [l.os.entregaTipo, l.os.transportadora].filter(Boolean).join(' · ');

  return h('tr', [
    h('td.pcp-mono', { style: { fontWeight: '600' } }, String(l.os.numero)),
    h('td', { style: { fontWeight: '500' } }, l.os.clienteCurto),
    h('td', [
      h('.pcp-produto', { title: l.os.produto }, [
        h('span.t-secondary.pcp-num', `${formato.numero(l.os.quantidade)} · `),
        l.os.produto,
      ]),
      marcasDaOs(l).length > 0
        ? h('.hstack.gap-1', { style: { marginTop: '3px', flexWrap: 'wrap' } }, marcasDaOs(l))
        : null,
    ]),
    h('td.t-callout', combinado || h('span.t-tertiary', 'não informado')),
    h('td', [
      h('div.t-callout', formato.dataHoraCurta(l.os.previsaoEntregaEm)),
      h(
        'div.t-subhead',
        { style: { color: l.atrasada ? 'var(--sys-red)' : 'var(--label-3)' } },
        formato.distanciaNoTempo(l.os.previsaoEntregaEm),
      ),
    ]),
    h('td.t-callout', l.os.prontaEm
      ? h('span', { title: formato.dataHoraCurta(l.os.prontaEm) }, formato.distanciaNoTempo(l.os.prontaEm))
      : h('span.t-tertiary', '—')),
    h('td', h('.hstack.gap-2', { style: { justifyContent: 'flex-end' } }, [
      botao('Abrir', () => app.roteador.ir(`/os/${l.os.numero}`), { pequeno: true }),
      botao('Registrar saída', () => void despachar(app, l), {
        estilo: 'primary',
        pequeno: true,
        icone: 'shippingbox',
      }),
    ])),
  ]);
}

/**
 * Registrar a saída pede confirmação porque conclui a OS, e concluir é o fim da
 * linha: a OS sai da carteira, sai do quadro e passa a contar na pontualidade.
 */
async function despachar(app: Aplicacao, l: LinhaCarteira): Promise<void> {
  const r = await folhaDeExpedicao(l);
  if (!r) return;

  await app.repo.registrarExpedicao({
    osNumero: l.os.numero,
    tipo: r.tipo,
    quemLevou: r.quemLevou,
    transportadora: r.transportadora,
    pessoaId: app.sessao.pessoaId,
    observacao: r.observacao,
  });

  notificar({
    titulo: `OS ${l.os.numero} ${r.tipo === 'retirada' ? 'retirada' : 'expedida'}`,
    corpo: 'A OS foi concluída e saiu da carteira.',
    icone: 'shippingbox',
  });
  app.redesenhar();
}

function blocoRecentes(
  app: Aplicacao,
  recentes: Expedicao[],
  carteira: LinhaCarteira[],
): HTMLElement {
  const nomeDe = (numero: number): string =>
    carteira.find((l) => l.os.numero === numero)?.os.clienteCurto ?? '—';

  return h('.cartao', [
    tituloDeSecao('Últimas saídas'),
    h('.tab-rolagem', [
      h('table.tab', [
        h('thead', h('tr', [
          h('th', { style: { width: '74px' } }, 'OS'),
          h('th', { style: { width: '180px' } }, 'Cliente'),
          h('th', { style: { width: '110px' } }, 'Como'),
          h('th', 'Quem levou / transportadora'),
          h('th', { style: { width: '150px' } }, 'Quando'),
        ])),
        h('tbody', recentes.map((e) =>
          h('tr.clicavel', { onclick: () => app.roteador.ir(`/os/${e.osNumero}`) }, [
            h('td.pcp-mono', { style: { fontWeight: '600' } }, String(e.osNumero)),
            h('td', nomeDe(e.osNumero)),
            h('td', h(`span.selo.${e.tipo === 'retirada' ? 's-azul' : 's-verde'}`,
              e.tipo === 'retirada' ? 'Retirada' : 'Expedida')),
            h('td.t-callout', [
              e.quemLevou || e.transportadora || h('span.t-tertiary', '—'),
              e.observacao ? h('div.t-subhead.t-secondary', e.observacao) : null,
            ]),
            h('td.t-callout', formato.dataHoraCurta(e.ocorridoEm)),
          ]),
        )),
      ]),
    ]),
  ]);
}
